import { defineComponent, PropType } from "vue";

import ImageFallback from "../../../components/imageFallback.jsx";
import {
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag,
} from "../../../utils.js";
import type { MemberWithRoles } from "../types.js";
import {
    getProfileBadges,
    roleColorGradientToCss,
    roleColorToCss,
    sortProfileRoles,
} from "./profileHelpers.js";

export const UserProfileCard = defineComponent({
    name: "UserProfileCard",

    props: {
        member: {
            type: Object as PropType<MemberWithRoles>,
            required: true,
        },
    },

    setup(props) {
        function renderRoleIcon(role: MemberWithRoles["roles"][number]) {
            if (role.iconURL)
                return <img class="user-profile-role-icon" src={role.iconURL} alt="" />;

            if (role.unicodeEmoji)
                return <span class="user-profile-role-emoji">{role.unicodeEmoji}</span>;

            return null;
        }

        return () => {
            const roles = sortProfileRoles(props.member.roles)
                .filter((role) => role.name !== "@everyone");
            const badges = getProfileBadges(props.member.user.bot, props.member.user.badges);

            return (
                <aside class="user-profile-card">
                    <div class="user-profile-card-banner" />
                    <div class="user-profile-card-body">
                        <ImageFallback
                            class="user-profile-card-avatar"
                            src={getMemberAvatar(props.member)}
                            default="https://cdn.discordapp.com/embed/avatars/0.png"
                            alt={getMemberDisplayName(props.member)}
                        />

                        <div class="user-profile-card-identity">
                            <h1>{getMemberDisplayName(props.member)}</h1>
                            <span>{getMemberTag(props.member)}</span>
                            <small>ID {props.member.id}</small>
                            {badges.length > 0 && (
                                <div class="user-profile-badge-list">
                                    {badges.map((badge) => (
                                        <span class="user-profile-badge" title={badge.label} key={badge.key}>
                                            <span class="material-symbols-outlined">{badge.icon}</span>
                                            {badge.label}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div class="user-profile-card-section">
                            <h2>Rôles</h2>
                            <div class="user-profile-role-list">
                                {roles.length
                                    ? roles.map((role) => (
                                        <span
                                            class="user-profile-role-chip"
                                            style={{
                                                "--role-color": roleColorToCss(role.color),
                                                "--role-dot-background": roleColorGradientToCss(role),
                                            }}
                                            key={role.id}
                                        >
                                            <span class="user-profile-role-dot" />
                                            {renderRoleIcon(role)}
                                            {role.name}
                                        </span>
                                    ))
                                    : <span class="user-profile-card-muted">Aucun rôle affichable.</span>}
                            </div>
                        </div>
                    </div>
                </aside>
            );
        };
    },
});
